import { useMemo } from 'react'
import { PAYMENT_TYPE_INT_TO_PAYMENTS_PAYMENT_TYPE } from 'Data/common-constants.js'
import { patientName } from 'Data/format.js'
import { v4 as uuid } from 'uuid'
import uniqBy from 'lodash/uniqBy.js'

/** @type {import('Simple/types.js').useDataTransform} */
export default function useDataTransform(props, data) {
  return useMemo(() => {
    if (!data) return data

    let ppa = data.patient.person?.person_payment_account

    let payment_accounts = uniqBy(
      [
        ...data.insureds.map(insured => {
          let {
            insurance_subscription: { person, insurance_plan },
          } = insured
          let { insurance_company } = insurance_plan
          let insurance_payment_account =
            insurance_company.insurance_payment_accounts[0]

          return {
            id: `${insurance_payment_account.id}`,
            // prettier-ignore
            text: `${insurance_company.carrier_name} (Group/Plan: ${insurance_plan.number}) - ${patientName(person)}`,
            data: {
              payer_person: person,
              payment_account_id: insurance_payment_account.id,
              insured_id: insured.id,
            },
          }
        }),
        ppa
          ? {
              id: `${ppa.id}`,
              text: `${patientName(ppa.payer_person)}`,
              data: {
                payer_person: ppa.payer_person,
                payment_account_id: ppa.id,
              },
            }
          : {
              id: uuid(),
              text: `${patientName(data.patient.person)} [NEW]`,
              data: {
                payer_person: data.patient.person,
                // new one will be created
                payment_account_id: null,
              },
            },
        ...(data.patient.person.to_person_relationships?.map(relationship => {
          let { from_person_record } = relationship
          let { person_payment_account } = from_person_record

          return {
            id: `${person_payment_account?.id ?? uuid()}`,
            // prettier-ignore
            text: `${patientName(from_person_record)}${!person_payment_account ? ' [NEW]' : ''}`,
            data: {
              payer_person: from_person_record,
              // when null, new one will be created
              payment_account_id: person_payment_account?.id ?? null,
            },
          }
        }) ?? []),
        ...(data.patient.receivables?.map(receivable => ({
          id: `${receivable.payment_account.person_payment_account.id}`,
          text: patientName(
            receivable.payment_account.person_payment_account.payer_person
          ),
          data: {
            payment_account_id:
              receivable.payment_account.person_payment_account.id,
            payer_person:
              receivable.payment_account.person_payment_account.payer_person,
          },
        })) ?? []),
        ...(data.patient.payments?.map(payment => ({
          id: `${payment.account.person_payment_account.id}`,
          text: patientName(
            payment.account.person_payment_account.payer_person
          ),
          data: {
            payment_account_id: payment.account.person_payment_account.id,
            payer_person: payment.account.person_payment_account.payer_person,
          },
        })) ?? []),
      ],
      'id'
    )

    payment_accounts.sort((a, b) => a.text.localeCompare(b.text))

    return {
      location_accounts: data.location_accounts,
      patient_due_amount: data.patient_due_amount?.[0]?.due_now ?? null,
      patient: data.patient,
      person: data.patient.person,
      receivable: data.receivable,
      payment_account: data.payment_account,
      payment_accounts,
      payment_methods: data.payment_methods.map(payment_type => ({
        id: payment_type.id,
        text: payment_type.name,
        data: {
          payment_type_id: payment_type.id,
          payments_payment_type:
            PAYMENT_TYPE_INT_TO_PAYMENTS_PAYMENT_TYPE[payment_type.type],
        },
      })),
    }
  }, [data])
}
